import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = checking, false = unauthenticated, object = user
  const [user, setUser] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // Defend against a misrouted response (e.g. baseURL was malformed and
      // SPA index.html came back as a string) — only accept proper JSON user.
      if (data && typeof data === "object" && data.id) {
        setUser(data);
      } else {
        setUser(false);
      }
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) localStorage.setItem("btt_token", data.token);
      // Login response is intentionally minimal — fetch the full user record
      // (agent_status, onboarding, phone, etc.) so the UI gates correctly.
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {
        setUser(data);
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name, referral_code, extras = {}) => {
    try {
      const payload = { email, password, name };
      if (referral_code && referral_code.trim()) {
        payload.referral_code = referral_code.trim().toUpperCase();
      }
      if (extras.phone) payload.phone = extras.phone.trim();
      if (extras.availability) payload.availability = extras.availability;
      if (extras.start_timeline) payload.start_timeline = extras.start_timeline;
      const { data } = await api.post("/auth/register", payload);
      if (data.token) localStorage.setItem("btt_token", data.token);
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
      } catch {
        setUser(data);
      }
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    localStorage.removeItem("btt_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, refresh: checkSession, refreshMe: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
